import { useContext, useMemo } from 'react';
import { PlusRounded } from '@icons';
import { Button } from '@ds';
import { HandleOpenNewLegModal } from '@lp/features/RoutePlanner/types';
import { AuthContext } from '@lp/core/auth';
import { AccommodationSegmentLeg } from '@lp/features/RoutePlanner/components/RouteViewList/AccommodationSegmentLeg';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { CaseStatus, RoutePlanStatus } from '@/api/apis/case';
import {
  SegmentTypeEnum,
  AviaLegTypes,
  GroundLegTypes,
  AccommodationLeg,
  RouteLegType,
  RouteTypeEnum,
} from '@/api/apis/route';
import { RouteBuilderPermissions } from '@/permissions';
import { isAllowedStatus } from '@/utils/helpers';
import { getSegmentMapKey } from '@/utils/route';
import { RouteFilterType, RouteSegmentType } from '@/types/route';
import { SegmentLegsList } from './SegmentLegsList';

export type RouteSegmentsListProps = {
  routeType: RouteTypeEnum;
  filterType: RouteFilterType;
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  accommodationLegs: Map<string, AccommodationLeg[]>;
  segments: RouteSegmentType[];
  handleAddNewLeg: (data: Partial<HandleOpenNewLegModal>) => void;
  handleHubAssign: (segmentType: SegmentTypeEnum) => void;
  handleRemoveHub: (segmentType: SegmentTypeEnum) => void;
  handleLegInfoClick: (leg: RouteLegType, segmentId: string | null, prevLegId: string | null) => void;
};

export const RouteSegmentsList = ({
  routeType,
  filterType,
  legs,
  accommodationLegs,
  segments,
  handleAddNewLeg,
  handleHubAssign,
  handleRemoveHub,
  handleLegInfoClick,
}: RouteSegmentsListProps) => {
  const { checkPermission } = useContext(AuthContext);
  const { routePlanningStatus, status } = useCaseDetailsWithReviews();
  const isEditableList = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.LegCreate);
    const isPlanningStatus = routePlanningStatus === RoutePlanStatus.RoutePlanningStart;
    const isFullRouteView = filterType === RouteFilterType.Full;
    const isActiveCaseStatus = isAllowedStatus({
      statusFrom: CaseStatus.PendingLogistics,
      statusTo: CaseStatus.ReturningToBase,
      currentStatus: status,
    });

    return hasPermission && isPlanningStatus && isFullRouteView && isActiveCaseStatus;
  }, [status, filterType, checkPermission, routePlanningStatus]);

  return (
    <div className={`flex flex-col ${!isEditableList ? 'pt-10' : 'pt-1'} gap-8 pb-8`}>
      {isEditableList && (
        <div className="flex items-center ml-32">
          <Button
            size="sm"
            variant="light"
            startContent={<PlusRounded />}
            onPress={() => handleAddNewLeg({ isNewSegment: true })}
          >
            <span className="text-s-sb">Add custom segment</span>
          </Button>
        </div>
      )}
      {!segments.length && (
        <div className="flex justify-center items-center">
          <span className="text-m-m">Route legs not found.</span>
        </div>
      )}
      {segments.map((segment) => {
        const isAccommodation = !!segment.isAccommodation;

        if (isAccommodation) {
          const currentAccommodationLegs = accommodationLegs.get(getSegmentMapKey(segment)) || [];

          return currentAccommodationLegs.map((leg) => {
            return <AccommodationSegmentLeg key={leg.id} leg={leg} handleLegInfoClick={handleLegInfoClick} />;
          });
        }

        const segmentMainLegs = legs.get(getSegmentMapKey(segment)) || [];
        const isCurrentSegmentReadOnly = !segment.startTime && segment.type === SegmentTypeEnum.hub;

        return (
          <SegmentLegsList
            key={segment.id}
            routeType={routeType}
            legs={segmentMainLegs}
            segment={segment}
            isReadOnly={!isEditableList}
            isDisabled={isCurrentSegmentReadOnly}
            disableHintText="Please fill in the DONOR - TXC segment first"
            handleAddNewLeg={handleAddNewLeg}
            handleHubAssign={handleHubAssign}
            handleRemoveHub={handleRemoveHub}
            handleLegInfoClick={handleLegInfoClick}
          />
        );
      })}
    </div>
  );
};
